/**
 * ConsoleLayout — shell for the system admin section at `/console`.
 *
 * Gated by `user.is_superuser`. Renders a left-hand nav with admin
 * sections; the first is "Document Templates" which hosts the authoring
 * UI at system scope.
 */
import { ReactNode } from 'react'
import { NavLink, Navigate, Outlet } from 'react-router-dom'
import UnifiedTopNav from '../../components/ui/UnifiedTopNav'
import { useUser } from '../../contexts/UserContext'
import { useTypedNavigate, ROUTES } from '../../lib/routing'
import { Shield, FileText, Workflow } from 'lucide-react'

interface Props {
  children?: ReactNode
}

const ConsoleLayout = ({ children }: Props) => {
  const { user, loading, logout } = useUser()
  const navigate = useTypedNavigate()

  if (loading) {
    return <div className="p-8 text-muted-foreground">Loading…</div>
  }

  if (!user) {
    return <Navigate to="/" replace />
  }

  if (!user.is_superuser) {
    // Soft-deny: redirect non-admins to home rather than showing a scary page.
    return <Navigate to="/home" replace />
  }

  const handleLogout = () => {
    logout()
    navigate(ROUTES.public.login)
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      <UnifiedTopNav
        onLogout={handleLogout}
        userName={user.full_name || 'Admin'}
        userOfficeName={user.office?.name || '—'}
      />
      <div className="flex-1 flex overflow-hidden">
        <aside className="w-60 border-r border-border bg-card flex flex-col">
          <div className="px-4 py-4 border-b border-border flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Admin Console</span>
          </div>
          <nav className="flex-1 p-2 space-y-1">
            <NavLink
              to="/console/document-templates"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground hover:bg-muted'
                }`
              }
            >
              <FileText className="w-4 h-4" />
              Document Templates
            </NavLink>
            <NavLink
              to="/console/workflows"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground hover:bg-muted'
                }`
              }
            >
              <Workflow className="w-4 h-4" />
              Workflow Designer
            </NavLink>
          </nav>
        </aside>
        <main className="flex-1 overflow-auto">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  )
}

export default ConsoleLayout
